import { Check, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { RiskLevel } from '../types';
import { RISK_LEVELS } from '../types';

interface RiskBadgeProps {
  risk: RiskLevel;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

export function RiskBadge({ risk, size = 'md', showLabel = true, className }: RiskBadgeProps) {
  const config = RISK_LEVELS[risk];

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const badgeSizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  const Icon = {
    check: Check,
    'alert-triangle': AlertTriangle,
    'x-circle': XCircle,
    'help-circle': HelpCircle,
  }[config.icon];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        config.bgColor,
        config.color,
        badgeSizes[size],
        className
      )}
    >
      <Icon className={iconSizes[size]} />
      {showLabel && <span>{config.labelDe}</span>}
    </div>
  );
}

interface RiskTrafficLightProps {
  risk: RiskLevel;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function RiskTrafficLight({ risk, size = 'md', className }: RiskTrafficLightProps) {
  const lightSizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  const containerSizes = {
    sm: 'p-1 gap-1',
    md: 'p-1.5 gap-1.5',
    lg: 'p-2 gap-2',
  };

  return (
    <div
      className={cn(
        'inline-flex flex-col items-center bg-gray-800 rounded-full',
        containerSizes[size],
        className
      )}
    >
      {/* Red */}
      <div
        className={cn(
          'rounded-full transition-all',
          lightSizes[size],
          risk === 'avoid' ? 'bg-red-500 shadow-lg shadow-red-500/50' : 'bg-red-900/50'
        )}
      />
      {/* Yellow */}
      <div
        className={cn(
          'rounded-full transition-all',
          lightSizes[size],
          risk === 'caution' ? 'bg-amber-500 shadow-lg shadow-amber-500/50' : 'bg-amber-900/50'
        )}
      />
      {/* Green */}
      <div
        className={cn(
          'rounded-full transition-all',
          lightSizes[size],
          risk === 'safe' ? 'bg-green-500 shadow-lg shadow-green-500/50' : 'bg-green-900/50'
        )}
      />
    </div>
  );
}
